import { apiSlice } from "../apiSlice";

export const masterSparesVariantApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ---------------------------------------------
    // 1) LIST VARIANTS
    // ---------------------------------------------
    getMasterSparesVariants: builder.query({
      query: ({ brand, model }) =>
        `masters/spares/variants?brand=${brand}&model=${model}`,

      transformResponse: (responseData) => {
        const variants = responseData?.data?.variants ?? [];
        return variants.map((item) => ({
          ...item,
          modifiedColor: item.color ? item.color.toUpperCase() : null,
          modifiedPartName: item.part_name
            ? item.part_name.toUpperCase()
            : null,
        }));
      },

      providesTags: (result) =>
        result
          ? [
              { type: "masterSparesVariant", id: "LIST" },
              ...result.map((item) => ({
                type: "masterSparesVariant",
                id: item.id,
              })),
            ]
          : [{ type: "masterSparesVariant", id: "LIST" }],
    }),

    // ---------------------------------------------
    // 2) GET VARIANT BY ID
    // ---------------------------------------------
    getMasterSparesVariantById: builder.query({
      query: (id) => `masters/spares/variants/${id}`,

      transformResponse: (responseData) => {
        const item = responseData?.data ?? {};
        return {
          ...item,
          modifiedColor: item.color ? item.color.toUpperCase() : null,
          modifiedPartName: item.part_name
            ? item.part_name.toUpperCase()
            : null,
        };
      },

      providesTags: (result, error, id) => [
        { type: "masterSparesVariant", id },
      ],
    }),

    // ---------------------------------------------
    // 3) ADD VARIANT (Optimistic Update)
    // ---------------------------------------------
    addMasterSparesVariant: builder.mutation({
      query: (payload) => ({
        url: `masters/spares`,
        method: "POST",
        body: payload,
      }),

      async onQueryStarted(payload, { dispatch, queryFulfilled }) {
        const { brand, model } = payload;

        // Optimistically add item using payload
        const patchResult = dispatch(
          apiSlice.util.updateQueryData(
            "getMasterSparesVariants",
            { brand, model },
            (draft) => {
              draft.push({
                ...payload,
                id: Math.random().toString(36).slice(2), // Fake ID
                modifiedColor: payload.color?.toUpperCase() || null,
                modifiedPartName: payload.part_name?.toUpperCase() || null,
              });
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },

      invalidatesTags: [{ type: "masterSparesVariant", id: "LIST" }],
    }),

    // ---------------------------------------------
    // 4) UPDATE VARIANT (Optimistic Update)
    // ---------------------------------------------
    updateMasterSparesVariant: builder.mutation({
      query: (body) => ({
        url: `masters/spares`,
        method: "PUT",
        body,
      }),

      async onQueryStarted(body, { dispatch, queryFulfilled }) {
        const { id, brand, model } = body;

        // Optimistically update
        const patchResult = dispatch(
          apiSlice.util.updateQueryData(
            "getMasterSparesVariants",
            { brand, model },
            (draft) => {
              const item = draft.find((x) => x.id === id);
              if (item) {
                Object.assign(item, {
                  ...body,
                  modifiedColor: body.color?.toUpperCase() || null,
                  modifiedPartName: body.part_name?.toUpperCase() || null,
                });
              }
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },

      invalidatesTags: (r, e, { id }) => [
        { type: "masterSparesVariant", id },
        { type: "masterSparesVariant", id: "LIST" },
      ],
    }),

    // ---------------------------------------------
    // 5) DELETE VARIANT (Optimistic Update)
    // ---------------------------------------------
    deleteMasterSparesVariant: builder.mutation({
      query: (id) => ({
        url: `masters/spares?id=${id}`,
        method: "DELETE",
      }),

      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        // Optimistically remove item
        const patchResult = dispatch(
          apiSlice.util.updateQueryData(
            "getMasterSparesVariants",
            {}, // brand/model dynamic? update accordingly
            (draft) => {
              const index = draft.findIndex((x) => x.id === id);
              if (index !== -1) draft.splice(index, 1);
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },

      invalidatesTags: (r, e, id) => [
        { type: "masterSparesVariant", id },
        { type: "masterSparesVariant", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetMasterSparesVariantsQuery,
  useGetMasterSparesVariantByIdQuery,
  useAddMasterSparesVariantMutation,
  useUpdateMasterSparesVariantMutation,
  useDeleteMasterSparesVariantMutation,
} = masterSparesVariantApi;
